/**
 * Trip Analytics Utilities
 * 
 * Functions for calculating trip statistics, spending trends, and analytics
 */

import type { Expense, ExpenseCategory } from '@/services/expenses';
import type { Booking } from '@/services/bookings';
import type { Trip } from '@/services/trips';
import { getExchangeRate } from '@/utils/currency';
import { getInclusiveDayCount } from '@/utils/dateKeys';

export type CategorySpending = {
  category: ExpenseCategory;
  amount: number;
  currency: string;
  count: number;
  percentage: number;
};

export type DailySpending = {
  date: string; // ISO date string
  amount: number;
  currency: string;
  expenseCount: number;
};

export type TripStatistics = {
  totalSpent: number;
  totalSpentConverted: number; // Converted to base currency
  baseCurrency: string;
  averagePerDay: number;
  totalExpenses: number;
  totalBookings: number;
  tripDuration: number; // in days
  startDate: Date;
  endDate: Date;
  mostExpensiveCategory: ExpenseCategory | null;
  mostExpensiveDay: string | null;
  topMerchants: Array<{ merchant: string; amount: number; count: number }>;
  topLocations: Array<{ location: string; amount: number; count: number }>;
};

export type SpendingTrend = {
  period: string; // e.g., "Week 1", "Day 1", etc.
  amount: number;
  expenseCount: number;
};

/**
 * Calculate spending by category
 */
export async function calculateCategorySpending(
  expenses: Expense[],
  baseCurrency: string
): Promise<CategorySpending[]> {
  const categoryTotals: Record<string, { amount: number; currency: string; count: number }> = {};

  // Aggregate expenses by category
  for (const expense of expenses) {
    const category = expense.category || 'other';
    if (!categoryTotals[category]) {
      categoryTotals[category] = { amount: 0, currency: expense.currency, count: 0 };
    }
    categoryTotals[category].count += 1;
    
    // Convert to base currency if needed
    if (expense.currency === baseCurrency) {
      categoryTotals[category].amount += expense.amount;
    } else {
      try {
        const rate = await getExchangeRate(expense.currency, baseCurrency);
        categoryTotals[category].amount += expense.amount * rate;
      } catch (error) {
        console.error(`Error converting ${expense.currency} to ${baseCurrency}:`, error);
        // Fallback: use amount as-is (not ideal but better than crashing)
        categoryTotals[category].amount += expense.amount;
      }
    }
  }

  // Calculate total for percentage calculation
  const total = Object.values(categoryTotals).reduce((sum, cat) => sum + cat.amount, 0);

  // Convert to array and calculate percentages
  return Object.entries(categoryTotals)
    .map(([category, data]) => ({
      category: category as ExpenseCategory,
      amount: data.amount,
      currency: baseCurrency,
      count: data.count,
      percentage: total > 0 ? (data.amount / total) * 100 : 0,
    }))
    .sort((a, b) => b.amount - a.amount);
}

/**
 * Calculate daily spending
 */
export async function calculateDailySpending(
  expenses: Expense[],
  baseCurrency: string
): Promise<DailySpending[]> {
  const dailyTotals: Record<string, { amount: number; currency: string; count: number }> = {};

  for (const expense of expenses) {
    const dateKey = expense.date.toISOString().split('T')[0]; // YYYY-MM-DD
    
    if (!dailyTotals[dateKey]) {
      dailyTotals[dateKey] = { amount: 0, currency: expense.currency, count: 0 };
    }
    
    dailyTotals[dateKey].count += 1;
    
    // Convert to base currency if needed
    if (expense.currency === baseCurrency) {
      dailyTotals[dateKey].amount += expense.amount;
    } else {
      try {
        const rate = await getExchangeRate(expense.currency, baseCurrency);
        dailyTotals[dateKey].amount += expense.amount * rate;
      } catch (error) {
        console.error(`Error converting ${expense.currency} to ${baseCurrency}:`, error);
        dailyTotals[dateKey].amount += expense.amount;
      }
    }
  }

  return Object.entries(dailyTotals)
    .map(([date, data]) => ({
      date,
      amount: data.amount,
      currency: baseCurrency,
      expenseCount: data.count,
    }))
    .sort((a, b) => a.date.localeCompare(b.date));
}

/**
 * Calculate comprehensive trip statistics
 */
export async function calculateTripStatistics(
  trip: Trip,
  expenses: Expense[],
  bookings: Booking[]
): Promise<TripStatistics> {
  const baseCurrency = trip.baseCurrency || 'USD';
  const startDate = trip.startDate;
  const endDate = trip.endDate;
  const tripDuration = getInclusiveDayCount(startDate, endDate) || 1;

  // Calculate total spending from expenses
  let totalSpent = 0;
  let totalSpentConverted = 0;
  const expenseAmountsByCurrency: Record<string, number> = {};

  for (const expense of expenses) {
    if (!expenseAmountsByCurrency[expense.currency]) {
      expenseAmountsByCurrency[expense.currency] = 0;
    }
    expenseAmountsByCurrency[expense.currency] += expense.amount;
  }

  // Convert all expenses to base currency
  for (const [currency, amount] of Object.entries(expenseAmountsByCurrency)) {
    if (currency === baseCurrency) {
      totalSpentConverted += amount;
    } else {
      try {
        const rate = await getExchangeRate(currency, baseCurrency);
        totalSpentConverted += amount * rate;
      } catch (error) {
        console.error(`Error converting ${currency} to ${baseCurrency}:`, error);
        totalSpentConverted += amount;
      }
    }
  }

  // Add booking amounts
  for (const booking of bookings) {
    if (booking.amount && booking.currency) {
      if (booking.currency === baseCurrency) {
        totalSpentConverted += booking.amount;
      } else {
        try {
          const rate = await getExchangeRate(booking.currency, baseCurrency);
          totalSpentConverted += booking.amount * rate;
        } catch (error) {
          console.error(`Error converting booking ${booking.currency} to ${baseCurrency}:`, error);
          totalSpentConverted += booking.amount;
        }
      }
    }
  }

  totalSpent = totalSpentConverted;
  const averagePerDay = totalSpent / tripDuration;

  // Find most expensive category
  const categorySpending = await calculateCategorySpending(expenses, baseCurrency);
  const mostExpensiveCategory = categorySpending.length > 0 ? categorySpending[0].category : null;

  // Find most expensive day
  const dailySpending = await calculateDailySpending(expenses, baseCurrency);
  const mostExpensiveDay = dailySpending.length > 0
    ? dailySpending.reduce((max, day) => (day.amount > max.amount ? day : max), dailySpending[0]).date
    : null;

  // Calculate top merchants
  const merchantTotals: Record<string, { amount: number; count: number }> = {};
  for (const expense of expenses) {
    if (expense.merchant) {
      if (!merchantTotals[expense.merchant]) {
        merchantTotals[expense.merchant] = { amount: 0, count: 0 };
      }
      merchantTotals[expense.merchant].count += 1;
      
      // Convert to base currency
      if (expense.currency === baseCurrency) {
        merchantTotals[expense.merchant].amount += expense.amount;
      } else {
        try {
          const rate = await getExchangeRate(expense.currency, baseCurrency);
          merchantTotals[expense.merchant].amount += expense.amount * rate;
        } catch (error) {
          merchantTotals[expense.merchant].amount += expense.amount;
        }
      }
    }
  }

  const topMerchants = Object.entries(merchantTotals)
    .map(([merchant, data]) => ({ merchant, ...data }))
    .sort((a, b) => b.amount - a.amount)
    .slice(0, 5);

  // Calculate top locations
  const locationTotals: Record<string, { amount: number; count: number }> = {};
  for (const expense of expenses) {
    if (expense.location) {
      if (!locationTotals[expense.location]) {
        locationTotals[expense.location] = { amount: 0, count: 0 };
      }
      locationTotals[expense.location].count += 1;
      
      // Convert to base currency
      if (expense.currency === baseCurrency) {
        locationTotals[expense.location].amount += expense.amount;
      } else {
        try {
          const rate = await getExchangeRate(expense.currency, baseCurrency);
          locationTotals[expense.location].amount += expense.amount * rate;
        } catch (error) {
          locationTotals[expense.location].amount += expense.amount;
        }
      }
    }
  }

  const topLocations = Object.entries(locationTotals)
    .map(([location, data]) => ({ location, ...data }))
    .sort((a, b) => b.amount - a.amount)
    .slice(0, 5);

  return {
    totalSpent,
    totalSpentConverted,
    baseCurrency,
    averagePerDay,
    totalExpenses: expenses.length,
    totalBookings: bookings.length,
    tripDuration,
    startDate,
    endDate,
    mostExpensiveCategory,
    mostExpensiveDay,
    topMerchants,
    topLocations,
  };
}

/**
 * Calculate spending trends (weekly or daily breakdown)
 */
export async function calculateSpendingTrends(
  expenses: Expense[],
  startDate: Date,
  endDate: Date,
  baseCurrency: string,
  period: 'daily' | 'weekly' = 'daily'
): Promise<SpendingTrend[]> {
  const trends: Record<string, { amount: number; expenseCount: number }> = {};

  for (const expense of expenses) {
    let periodKey: string;
    
    if (period === 'weekly') {
      // Calculate week number
      const weekStart = new Date(startDate);
      const weekNumber = Math.floor((expense.date.getTime() - weekStart.getTime()) / (7 * 24 * 60 * 60 * 1000)) + 1;
      periodKey = `Week ${weekNumber}`;
    } else {
      // Daily
      const dayNumber = Math.floor((expense.date.getTime() - startDate.getTime()) / (24 * 60 * 60 * 1000)) + 1;
      periodKey = `Day ${dayNumber}`;
    }

    if (!trends[periodKey]) {
      trends[periodKey] = { amount: 0, expenseCount: 0 };
    }

    trends[periodKey].expenseCount += 1;

    // Convert to base currency
    if (expense.currency === baseCurrency) {
      trends[periodKey].amount += expense.amount;
    } else {
      try {
        const rate = await getExchangeRate(expense.currency, baseCurrency);
        trends[periodKey].amount += expense.amount * rate;
      } catch (error) {
        trends[periodKey].amount += expense.amount;
      }
    }
  }

  return Object.entries(trends)
    .map(([period, data]) => ({
      period,
      amount: data.amount,
      expenseCount: data.expenseCount,
    }))
    .sort((a, b) => {
      // Sort by period number
      const aNum = parseInt(a.period.match(/\d+/)?.[0] || '0');
      const bNum = parseInt(b.period.match(/\d+/)?.[0] || '0');
      return aNum - bNum;
    });
}

/**
 * Get favorite destinations from user's trips
 */
export function getFavoriteDestinations(trips: Trip[]): Array<{ destination: string; count: number }> {
  const destinationCounts: Record<string, number> = {};

  for (const trip of trips) {
    if (trip.destination) {
      // Handle multi-destination trips (comma-separated)
      const destinations = trip.destination.split(',').map(d => d.trim());
      for (const dest of destinations) {
        if (dest) {
          destinationCounts[dest] = (destinationCounts[dest] || 0) + 1;
        }
      }
    }
  }

  return Object.entries(destinationCounts)
    .map(([destination, count]) => ({ destination, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 10); // Top 10 destinations
}

